import React, { useState, useRef } from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { 
  Plus, 
  ChevronDown,
  CheckSquare, 
  GitBranch, 
  Users, 
  Building2, 
  Tag, 
  Mail, 
  MessageSquare 
} from "lucide-react"
import { useUsers } from "@/lib/contexts/user-context"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import { useToast } from "@/hooks/use-toast"

export function QuickActionsMenu() {
  const { currentUser: contextCurrentUser } = useUsers()
  const { props } = usePage()
  const currentUser = props.user || contextCurrentUser
  const permissions = props.permissions || {}
  const { createWorkflow } = useWorkflow()
  const { toast } = useToast()
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false)
  const createWorkflowLock = useRef(false)

  // Get user permissions from context
  const userPermissions = currentUser?.permissions || []
  const hasPermission = (permission) => userPermissions.includes(permission)
  const isAdmin = currentUser?.roles?.includes('admin')

  // Check view permissions (matching app-sidebar.jsx logic)
  const canViewTasks = hasPermission('view_own_tasks') || 
                      hasPermission('view_department_tasks') || 
                      hasPermission('view_all_tasks')
  const canViewWorkflows = hasPermission('view_workflows')
  const canViewUsers = hasPermission('view_department_users') || isAdmin
  const canViewDepartments = hasPermission('view_departments')
  const canViewCategories = isAdmin // Categories are admin-only
  const canViewEmailTemplates = hasPermission('view_email_templates')
  const canViewTextTemplates = hasPermission('view_text_templates')

  // Check create permissions
  const canCreateTasks = permissions.can_create_tasks || hasPermission('create_tasks')
  const canCreateWorkflows = permissions.can_create_workflows || hasPermission('create_workflows')
  const canCreateUsers = permissions.can_create_users || hasPermission('create_users')
  const canCreateDepartments = permissions.can_create_departments || hasPermission('create_departments')
  const canCreateCategories = isAdmin // Categories are admin-only
  const canCreateEmailTemplates = permissions.can_create_email_templates || hasPermission('create_email_templates')
  const canCreateTextTemplates = permissions.can_create_text_templates || hasPermission('create_text_templates')

  // Define available actions with their permissions (require both view AND create)
  const actions = [
    {
      id: 'task',
      label: 'New Task',
      icon: CheckSquare,
      path: '/tasks',
      canAccess: canViewTasks && canCreateTasks,
    },
    {
      id: 'workflow',
      label: 'New Workflow',
      icon: GitBranch,
      path: '/workflows',
      canAccess: canViewWorkflows && canCreateWorkflows,
    },
    {
      id: 'user',
      label: 'Add User',
      icon: Users,
      path: '/users',
      canAccess: canViewUsers && canCreateUsers,
    },
    {
      id: 'department',
      label: 'Add Department',
      icon: Building2,
      path: '/departments',
      canAccess: canViewDepartments && canCreateDepartments,
    },
    {
      id: 'category',
      label: 'Add Category',
      icon: Tag,
      path: '/categories',
      canAccess: canViewCategories && canCreateCategories,
    },
    {
      id: 'email-template',
      label: 'Add Email Template',
      icon: Mail,
      path: '/email-templates',
      canAccess: canViewEmailTemplates && canCreateEmailTemplates,
    },
    {
      id: 'text-template',
      label: 'Add Text Template',
      icon: MessageSquare,
      path: '/text-templates',
      canAccess: canViewTextTemplates && canCreateTextTemplates,
    },
  ]

  // Filter to only actions the user can access
  const availableActions = actions.filter(action => action.canAccess)

  // Don't render if user has no create permissions
  if (!currentUser || availableActions.length === 0) {
    return null
  }

  const handleWorkflowCreate = async () => {
    if (createWorkflowLock.current || isCreatingWorkflow) return;
    
    createWorkflowLock.current = true;
    setIsCreatingWorkflow(true);
    
    try {
      const creator = `${currentUser.firstName} ${currentUser.lastName}`;
      const now = new Date();
      const formattedDate = now.toLocaleString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true 
      });
      const name = `Workflow - ${creator} - ${formattedDate}`;
      
      // Get CSRF token from meta tag
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      // Create workflow with individual assignment to current user
      const createdWorkflow = await createWorkflow(
        name, 
        csrfToken, 
        'individual', 
        currentUser?.id ? [currentUser.id] : []
      );
      
      if (createdWorkflow && createdWorkflow.id) {
        // Navigate directly to the workflow builder
        router.visit(`/workflows/${createdWorkflow.id}`);
      }
    } catch (error) {
      console.error('Failed to create workflow:', error);
      toast({
        title: "Error",
        description: "Failed to create workflow. Please try again.",
        variant: "destructive",
      });
    } finally {
      createWorkflowLock.current = false;
      setIsCreatingWorkflow(false);
    }
  };

  const handleAction = (action) => {
    // Special handling for workflow creation
    if (action.id === 'workflow') {
      handleWorkflowCreate();
    } else {
      router.visit(`${action.path}?action=new`);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-9 px-3 rounded-md border focus:ring-0 focus:ring-offset-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none">
          <Plus className="h-4 w-4 mr-1.5" />
          <ChevronDown className="h-3.5 w-3.5" />
          <span className="sr-only">Quick actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 z-50" align="end" forceMount>
        <DropdownMenuGroup>
          {availableActions.map((action) => {
            const Icon = action.icon
            const isWorkflow = action.id === 'workflow'
            return (
              <DropdownMenuItem
                key={action.id}
                onSelect={() => handleAction(action)}
                className="cursor-pointer"
                disabled={isWorkflow && isCreatingWorkflow}
              >
                <Icon className="mr-2 h-4 w-4" />
                <span>{isWorkflow && isCreatingWorkflow ? "Creating..." : action.label}</span>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
